/**
 * ViewBodyEditFormBody — shared form body for editing a view's name, body,
 * and engine-specific options.
 *
 * Engines:
 *   - mssql: emits CREATE OR ALTER VIEW [WITH SCHEMABINDING] AS …
 *   - postgres: emits CREATE OR REPLACE VIEW [WITH (check_option = …,
 *     security_barrier = …)] AS …
 *   - mysql: emits CREATE OR REPLACE [ALGORITHM = …] [DEFINER = …]
 *     [SQL SECURITY {DEFINER|INVOKER}] VIEW … AS … [WITH … CHECK OPTION].
 *   - oracle: emits CREATE OR REPLACE [EDITIONABLE|NONEDITIONABLE] [FORCE]
 *     VIEW … AS …
 *   - sqlite: shows a disabled affordance with the documented DROP-and-CREATE
 *     redirect tooltip (no atomic ALTER VIEW).
 *
 * The owning popover / embed seeds `viewName`, `initialBody`, and
 * `initialOptions` from the parked statement text. The form is the same body
 * whether mounted inline (Variant 1) or in the Designer pane via
 * `ViewBodyEmbed` (Variant 2).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D2 — View / Materialized View UI.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Textarea } from '../../../../../components/ui/textarea';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  commitAlterView,
  getAlterViewUnsupportedTooltip,
  isAlterViewSupported,
  validateAlterViewForEngine,
} from '../../edit/edits/alterView';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ViewOptionsFields, type ViewOptionsValue } from './ViewOptionsFields';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export interface ViewBodyEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly viewName: QualifiedName;
  readonly initialBody?: string;
  readonly initialOptions?: ViewOptionsValue;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

/** Split the comma-separated user input into a clean column-alias list.
 *  Trims surrounding whitespace per entry, drops blanks, and returns `[]` when
 *  nothing remains so the synthesizer skips the parenthesized clause. */
function parseColumnsInput(text: string): readonly string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function stripColumns(options: ViewOptionsValue | undefined): ViewOptionsValue {
  if (options === undefined) return {};
  const next: Record<string, unknown> = { ...options };
  delete next.columns;
  return next as ViewOptionsValue;
}

export function ViewBodyEditFormBody(props: ViewBodyEditFormBodyProps) {
  const {
    editContext, viewName, initialBody, initialOptions, testId, onCommitted, onCancel,
  } = props;
  const engine = editContext.service.engineId;

  const [name, setName] = useState(viewName.name);
  const [bodyText, setBodyText] = useState(initialBody ?? '');
  const initialColumns = initialOptions?.columns;
  const [columnsText, setColumnsText] = useState(
    initialColumns && initialColumns.length > 0 ? initialColumns.join(', ') : '',
  );
  // `columns` is owned by the standalone input below — strip it from the
  // engine-options state so the field isn't double-sourced on submit.
  const [options, setOptions] = useState<ViewOptionsValue>(() =>
    stripColumns(initialOptions),
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!isAlterViewSupported(engine)) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <div className="text-xs font-semibold">Edit view body</div>
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-unsupported`}
        >
          {getAlterViewUnsupportedTooltip(engine)}
        </div>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCancel?.()}
            data-testid={`${testId}-close`}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  async function handleSubmit() {
    setError(null);
    const columns = parseColumnsInput(columnsText);
    const mergedOptions: ViewOptionsValue =
      columns.length > 0 ? { ...options, columns } : options;
    const args = {
      viewName: viewName.name === name.trim()
        ? viewName
        : { ...viewName, name: name.trim(), foldedName: name.trim().toLowerCase() },
      bodyText: bodyText.trimEnd(),
      viewOptions: mergedOptions,
    };
    const validationError = validateAlterViewForEngine(engine, {
      viewName: args.viewName,
      bodyText: args.bodyText,
    });
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitAlterView(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const submitLabel = engine === 'mssql' ? 'Save (OR ALTER)' : 'Save (OR REPLACE)';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Edit view body</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          View name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="v_orders"
          className="h-8 font-mono text-xs"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-columns`} className="text-xs">
          Column aliases (optional)
        </Label>
        <Input
          id={`${testId}-columns`}
          data-testid={`${testId}-columns`}
          value={columnsText}
          onChange={(e) => setColumnsText(e.target.value)}
          placeholder="customer_id, customer_email"
          className="h-8 font-mono text-xs"
        />
        <span className="text-[10px] text-muted-foreground">
          Comma-separated list. Renames the view's output columns —
          e.g. <code>CREATE VIEW v (customer_id, customer_email) AS …</code>.
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-body`} className="text-xs">
          Body (SELECT) <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id={`${testId}-body`}
          data-testid={`${testId}-body`}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          placeholder="SELECT id, name FROM customer WHERE active = 1"
          className="min-h-[120px] font-mono text-xs"
        />
      </div>

      <ViewOptionsFields
        engine={engine}
        value={options}
        onChange={setOptions}
        testId={`${testId}-options`}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}
